/**
 * When is the app allowed to call the API under Entra?
 *
 * msal-react seeds `accounts` synchronously from the session cache while `inProgress` is still
 * `startup` (see @azure/msal-react MsalProvider: `accounts: instance.getAllAccounts()`), so
 * "an account exists" is NOT the same as "a token exists". Gating on the account alone let the
 * shell mount and fire queries before any bearer token had been acquired.
 */
export interface EntraInputs {
  hasAccount: boolean;
  /** msal-react's InteractionStatus, compared as a string so this stays dependency-free. */
  inProgress: string;
  /** An acquireTokenSilent for the API scope has resolved at least once. */
  tokenReady: boolean;
}

export interface AuthResolution {
  /** MSAL has finished deciding: render the app or the sign-in screen, not a splash. */
  ready: boolean;
  /** A usable credential exists — only now may queries run. */
  signedIn: boolean;
}

export function resolveEntraAuth({ hasAccount, inProgress, tokenReady }: EntraInputs): AuthResolution {
  const settled = inProgress === 'none';
  if (!settled) return { ready: false, signedIn: false };
  // No account: MSAL is done and the answer is "nobody is signed in" — show sign-in, not a splash.
  if (!hasAccount) return { ready: true, signedIn: false };
  // Account present: wait for the token before letting anything call the API.
  return { ready: tokenReady, signedIn: tokenReady };
}
